import React, { useEffect, useState } from 'react';
import { Box, ActionMenu, ActionList, IconButton, useConfirm } from '@primer/react';
import { PlusIcon, SyncIcon, PencilIcon, TrashIcon, KebabHorizontalIcon } from '@primer/octicons-react';

import DataTable from '../../../components/ui/data-table/DataTable';
import { useCommonContext } from '../../../contexts/common';

import { ts_hocsinh_lichsu_yte_donvitinh } from '../../../models/response/category/ts_hocsinh_lichsu_yte_donvitinh';
import { donViTinhThuocApi } from '../../../api/donViTinhThuocApi';
import { NotifyHelper } from '../../../helpers/toast';
import Button from '../../../components/ui/button';
import TextTranslated from '../../../components/text';
import DonViTinhThuocEditModal from './DonViTinhThuocEditModal';

const DonViTinhThuocPage: React.FC = () => {
    const { translate, checkAccesiableTo } = useCommonContext();
    const confirm = useConfirm();

    // State
    const [dataSource, setDataSource] = useState<ts_hocsinh_lichsu_yte_donvitinh[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isShowEditModal, setIsShowEditModal] = useState(false);
    const [editingData, setEditingData] = useState<ts_hocsinh_lichsu_yte_donvitinh | undefined>(undefined);

    // Phân quyền API
    // const canView = checkAccesiableTo("don-vi-tinh-thuoc", "GET");
    // const canAdd = checkAccesiableTo("don-vi-tinh-thuoc", "POST");
    // const canEdit = checkAccesiableTo("don-vi-tinh-thuoc", "PUT");
    // const canDelete = checkAccesiableTo("don-vi-tinh-thuoc/{id}", "DELETE");

    const canView = true;
    const canAdd = true;
    const canEdit = true;
    const canDelete = true;

    // Load data
    const loadData = async () => {
        if (!canView) return;

        try {
            setIsLoading(true);
            const response = await donViTinhThuocApi.selectAll();
            setDataSource(response.data || []);
        } catch (error: any) {
            NotifyHelper.Error(error.message || "Có lỗi xảy ra khi tải dữ liệu");
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        loadData();
    }, [canView]);

    const handleReload = () => {
        loadData();
    };

    const handleAdd = () => {
        setEditingData(undefined);
        setIsShowEditModal(true);
    };

    const handleEdit = (data: ts_hocsinh_lichsu_yte_donvitinh) => {
        setEditingData(data);
        setIsShowEditModal(true);
    };

    const handleDelete = async (id: number) => {
        if (await confirm({
            content: translate("Base.Label.DeleteConfirm.Content"),
            title: translate("Base.Label.Confirm"),
            cancelButtonContent: translate("Base.Label.Close"),
            confirmButtonContent: translate("Base.Label.Delete"),
            confirmButtonType: "danger"
        })) {
            try {
                setIsLoading(true);
                await donViTinhThuocApi.delete(id);
                NotifyHelper.Success("Xóa thành công");
                await loadData();
            } catch (error: any) {
                NotifyHelper.Error(error.message || "Có lỗi xảy ra khi xóa dữ liệu");
            } finally {
                setIsLoading(false);
            }
        }
    };

    const handleSaveSuccess = () => {
        setIsShowEditModal(false);
        setEditingData(undefined);
        loadData();
    };

    const handleCloseModal = () => {
        setIsShowEditModal(false);
        setEditingData(undefined);
    };

    return (
        <Box sx={{ p: 3 }}>
            <DataTable
                data={dataSource}
                isLoading={isLoading}
                title="Danh sách đơn vị tính thuốc"
                height="calc(100vh - 220px)"
                searchEnable
                paging={{
                    enable: true,
                    pageSizeItems: [20, 50, 200, 500, 1000],
                }}
                actionComponent={
                    <Box sx={{ display: "flex" }}>
                        {canAdd && (
                            <Button
                                text="Base.Label.AddNew"
                                leadingVisual={PlusIcon}
                                size="medium"
                                variant="primary"
                                onClick={handleAdd}
                            />
                        )}
                        {canView && (
                            <Button
                                text="Base.Label.Refresh"
                                leadingVisual={SyncIcon}
                                size="medium"
                                sx={{ ml: canAdd ? 1 : 0 }}
                                onClick={handleReload}
                            />
                        )}
                    </Box>
                }
                columns={[
                    {
                        id: "cmd",
                        width: "50px",
                        caption: "#",
                        align: "center",
                        cellRender: (data: ts_hocsinh_lichsu_yte_donvitinh) => {
                            const hasEditPermission = canEdit;
                            const hasDeletePermission = canDelete;

                            // Hiển thị box trống nếu không có quyền nào
                            if (!hasEditPermission && !hasDeletePermission) {
                                return <Box sx={{ m: -1 }}></Box>;
                            }

                            return (
                                <Box sx={{ m: -1 }}>
                                    <ActionMenu>
                                        <ActionMenu.Anchor>
                                            <IconButton
                                                icon={KebabHorizontalIcon}
                                                variant="invisible"
                                                aria-label="Open column options"
                                            />
                                        </ActionMenu.Anchor>

                                        <ActionMenu.Overlay>
                                            <ActionList>
                                                {hasEditPermission && (
                                                    <ActionList.Item
                                                        onSelect={() => handleEdit(data)}
                                                    >
                                                        <ActionList.LeadingVisual>
                                                            <PencilIcon />
                                                        </ActionList.LeadingVisual>
                                                        <TextTranslated value="Base.Label.Edit" />
                                                    </ActionList.Item>
                                                )}
                                                {hasDeletePermission && (
                                                    <>
                                                        {hasEditPermission && <ActionList.Divider />}
                                                        <ActionList.Item
                                                            variant="danger"
                                                            onSelect={() => handleDelete(data.id)}
                                                        >
                                                            <ActionList.LeadingVisual>
                                                                <TrashIcon />
                                                            </ActionList.LeadingVisual>
                                                            <TextTranslated value="Base.Label.Delete" />
                                                        </ActionList.Item>
                                                    </>
                                                )}
                                            </ActionList>
                                        </ActionMenu.Overlay>
                                    </ActionMenu>
                                </Box>
                            );
                        },
                    },
                    {
                        id: "ten_donvitinh",
                        caption: "Tên đơn vị tính",
                        dataField: "ten_donvitinh",
                        isMainColumn: true,
                        width: "300px",
                    },
                    {
                        id: "ten_donvitinh_en",
                        caption: "Tên đơn vị tính (EN)",
                        dataField: "ten_donvitinh_en",
                        width: "300px",
                    },
                ]}
            />
            {isShowEditModal && (
                <DonViTinhThuocEditModal
                    editingData={editingData}
                    onSaveSuccess={handleSaveSuccess}
                    onClose={handleCloseModal}
                />
            )}
        </Box>
    );
};

export default DonViTinhThuocPage;
